// Estado de seguridad de la sesión, con una caché corta en memoria.
//
// El candado de rutas (`SecurityGate`) lo consulta en cada cambio de pantalla;
// sin caché sería una petición por navegación. Vive 60 s, es por token (otra
// cuenta en el mismo navegador nunca hereda el estado de la anterior) y se tira
// en cuanto la persona cambia algo de su seguridad (`setSecurityStatus`).
//
// Falla ABIERTO: si no se puede leer, devuelve `null` y la pantalla no bloquea
// nada. El candado de verdad es el del backend (403 en cada ruta).

import { create } from "zustand";
import { fetchSecurityStatus, type SecurityStatus } from "./securityService";

const TTL_MS = 60 * 1000;

interface StatusState {
  status: SecurityStatus | null;
  set: (status: SecurityStatus | null) => void;
}

/** El último estado conocido, para los componentes que lo pintan. */
export const useSecurityStatusStore = create<StatusState>((set) => ({
  status: null,
  set: (status) => set({ status }),
}));

let cache: { token: string; at: number } | null = null;
let inFlight: { token: string; promise: Promise<SecurityStatus | null> } | null = null;

/** Guarda un estado recién devuelto por la API (toda escritura lo devuelve). */
export function setSecurityStatus(status: SecurityStatus): void {
  const token = localStorage.getItem("token");
  if (token) cache = { token, at: Date.now() };
  useSecurityStatusStore.getState().set(status);
}

/** Olvida el estado (cierre de sesión, o para forzar una lectura). */
export function clearSecurityStatus(): void {
  cache = null;
  inFlight = null;
  useSecurityStatusStore.getState().set(null);
}

/** Estado vigente; `force` se salta la caché. `null` si no hay sesión o falló. */
export async function loadSecurityStatus({ force = false } = {}): Promise<SecurityStatus | null> {
  const token = localStorage.getItem("token");
  if (!token) {
    clearSecurityStatus();
    return null;
  }
  const current = useSecurityStatusStore.getState().status;
  if (!force && current && cache && cache.token === token && Date.now() - cache.at < TTL_MS) return current;
  if (inFlight && inFlight.token === token) return inFlight.promise;

  const promise = fetchSecurityStatus()
    .then((status) => {
      // Entre que se pidió y llegó pudo cerrarse la sesión o entrar otra cuenta.
      if (localStorage.getItem("token") !== token) return null;
      setSecurityStatus(status);
      return status;
    })
    .catch(() => null)
    .finally(() => {
      if (inFlight && inFlight.promise === promise) inFlight = null;
    });
  inFlight = { token, promise };
  return promise;
}
